import React from 'react'
import {
  ChartLineUp,
  Package,
  SealPercent,
  Gift,
  Lightning,
  Megaphone,
  Scroll,
  HandWaving,
  Compass,
  UsersThree,
  DiamondsFour,
  Gear,
  Question,
  MoneyWavy,
  ShoppingCart,
  ShoppingBagOpen,
  Ticket,
  IconProps
} from '@phosphor-icons/react'
import { JourneyStep } from 'src/lib/hooks/useNavigation'
import { PDP_NAV_ICON_URL } from 'src/lib/utils/constants'

export interface SearchItem {
  title: string
  searchText: string[]
  path: string
  icon?: React.ComponentType<IconProps>
  rbacPermission: string | string[] | null
  category: 'main' | 'footer' | 'secondary'
  journeyStep?: JourneyStep
  children?: SearchItem[]
}

// The PDP entry uses the Flo PDP artwork rather than a Phosphor glyph; sized to the search row's
// 20px icon slot. Props are ignored — it renders an image, not an icon font.
const PdpIcon: React.ComponentType<IconProps> = () => (
  <img src={PDP_NAV_ICON_URL} alt='Flo PDP' className='w-5 h-5 rounded-md' />
)

export const searchItems: SearchItem[] = [
  // Main Navigation Items
  {
    title: 'Analytics',
    searchText: [
      'analytics',
      'dashboard',
      'charts',
      'reports',
      'metrics',
      'insights',
      'performance',
      'sales',
      'report'
    ],
    path: '/analytics',
    icon: ChartLineUp,
    rbacPermission: 'TAB_ANALYTICS',
    category: 'main'
  },
  {
    title: 'Overview',
    searchText: ['overview', 'dashboard', 'summary', 'home'],
    path: '/overview',
    icon: Compass,
    rbacPermission: 'TAB_OVERVIEW',
    category: 'main'
  },
  {
    title: 'Orders',
    searchText: ['orders', 'order', 'management', 'sales', 'transactions', 'customers'],
    path: '/orders',
    icon: Package,
    rbacPermission: 'TAB_ORDERS',
    category: 'main',
    children: [
      {
        title: 'Abandoned Checkout',
        searchText: ['abandoned', 'checkouts', 'abandoned', 'carts', 'acr', 'recovery', 'exit'],
        path: '/abandoned-checkouts',
        rbacPermission: 'TAB_ABANDONED_CHECKOUTS',
        category: 'main'
      },
      {
        title: 'Checkout Links',
        searchText: ['checkout', 'links', 'checkout', 'link', 'payment', 'link', 'share', 'cart'],
        path: '/checkout-links',
        rbacPermission: 'TAB_ORDERS',
        category: 'main'
      }
    ]
  },
  {
    title: 'Discount',
    searchText: ['discounts', 'coupons', 'offers', 'promotions', 'price', 'rules'],
    path: '/discounts',
    icon: SealPercent,
    rbacPermission: 'TAB_DISCOUNTS',
    category: 'main',
    children: [
      {
        title: 'Payment Offers',
        searchText: ['payment', 'offers'],
        path: '/payment_offers',
        rbacPermission: 'TAB_DISCOUNTS',
        category: 'main'
      },
      {
        title: 'Coupons',
        searchText: ['coupons', 'discount', 'codes', 'voucher', 'promo', 'code'],
        path: '/discounts#codes',
        rbacPermission: 'TAB_DISCOUNTS',
        category: 'main'
      },
      {
        title: 'Sets',
        searchText: ['discount', 'sets', 'discount', 'collections', 'bulk', 'discount'],
        path: '/discounts#sets',
        rbacPermission: 'TAB_DISCOUNTS',
        category: 'main'
      },
      {
        title: 'Callout Cards',
        searchText: ['callout', 'cards', 'discount', 'callouts', 'banners'],
        path: '/discounts#callout_cards',
        rbacPermission: 'TAB_DISCOUNTS',
        category: 'main'
      },
      {
        title: 'Exit Coupons',
        searchText: ['exit', 'coupons', 'exit coupon', 'discount', 'nudge'],
        path: '/discounts#exit_coupons',
        rbacPermission: 'TAB_DISCOUNTS',
        category: 'main'
      }
    ]
  },
  {
    title: 'Rewards',
    searchText: ['rewards', 'loyalty', 'points', 'gifts', 'referrals', 'customize'],
    path: '/rewards',
    icon: Gift,
    rbacPermission: 'TAB_REWARDS',
    category: 'main'
  },
  {
    title: 'Segments',
    searchText: ['segments', 'customer', 'segments', 'audience', 'groups', 'saved', 'searches'],
    path: '/segments',
    icon: UsersThree,
    rbacPermission: 'TAB_SEGMENTS',
    category: 'main'
  },
  {
    title: 'Ads IQ',
    searchText: ['ads', 'iq', 'meta', 'roas', 'marketing', 'advertising', 'signals'],
    path: '/ads-iq/real-estate',
    icon: Megaphone,
    rbacPermission: null,
    category: 'main'
  },
  {
    title: 'Automations',
    searchText: ['automations', 'workflow', 'automation', 'flow', 'triggers', 'campaigns', 'view'],
    path: '/automations',
    icon: Lightning,
    rbacPermission: 'TAB_AUTOMATIONS',
    category: 'main'
  },
  {
    title: 'Reports',
    searchText: ['reports', 'download', 'reports', 'analytics', 'export', 'data'],
    path: '/download/report',
    icon: Scroll,
    rbacPermission: 'TAB_DOWNLOAD_REPORTS',
    category: 'main'
  },
  {
    title: 'Onboarding',
    searchText: ['onboarding', 'setup', 'configuration'],
    path: '/onboarding-v2',
    icon: HandWaving,
    rbacPermission: 'TAB_ORDERS',
    category: 'main'
  },

  {
    title: 'Cart',
    searchText: ['cart', 'shopping', 'cart', 'checkout', 'basket'],
    path: '/cart',
    icon: ShoppingBagOpen,
    rbacPermission: 'TAB_CART',
    category: 'secondary',
    journeyStep: JourneyStep.CART,
    children: [
      {
        title: 'Core Setup',
        searchText: ['cart', 'setup', 'cart', 'configuration', 'cart', 'settings'],
        path: '/cart/ui',
        rbacPermission: 'CORE_SETUP_CART',
        category: 'secondary',
        journeyStep: JourneyStep.CART
      },
      {
        title: 'Coupons Page',
        searchText: ['cart', 'setup', 'cart', 'configuration', 'cart', 'settings', 'coupons page', 'page', 'coupons'],
        path: '/cart/coupons',
        rbacPermission: 'COUPONS_PAGE_CHECKOUT',
        category: 'secondary',
        journeyStep: JourneyStep.CART
      },
      {
        title: 'Widget',
        searchText: ['cart', 'widget', 'cart', 'component', 'mini', 'cart', 'drawer'],
        path: '/cart/widget',
        rbacPermission: 'CART_WIDGET',
        category: 'secondary',
        journeyStep: JourneyStep.CART
      },
      {
        title: 'Analytics',
        searchText: ['cart', 'analytics', 'cart', 'metrics', 'cart', 'reports'],
        path: '/cart/cart-analytics',
        rbacPermission: 'CART_ANALYTICS',
        category: 'secondary',
        journeyStep: JourneyStep.CART
      }
    ]
  },

  {
    title: 'PDP',
    searchText: ['pdp', 'product page', 'product detail', 'detail page'],
    path: '/pdp',
    icon: PdpIcon,
    rbacPermission: 'TAB_PDP',
    category: 'secondary',
    journeyStep: JourneyStep.PDP,
    children: [
      {
        title: 'Core Setup',
        searchText: ['pdp', 'setup', 'product page setup', 'pdp configuration'],
        path: '/pdp/core-setup',
        rbacPermission: 'CORE_SETUP_PDP_EDIT',
        category: 'secondary',
        journeyStep: JourneyStep.PDP
      },
      {
        title: 'Delivery Widget',
        searchText: ['pdp', 'delivery', 'widget', 'etd', 'estimated delivery date', 'pincode'],
        path: '/pdp/widgets/delivery',
        rbacPermission: 'CORE_SETUP_PDP_EDIT',
        category: 'secondary',
        journeyStep: JourneyStep.PDP
      },
      {
        title: 'Analytics',
        searchText: ['pdp', 'analytics', 'product page analytics', 'pdp metrics'],
        path: '/pdp/pdp-analytics',
        rbacPermission: 'PDP_ANALYTICS',
        category: 'secondary',
        journeyStep: JourneyStep.PDP
      }
    ]
  },

  // Checkout Sections
  {
    title: 'Checkout',
    searchText: ['checkout', 'payment', 'process', 'checkout', 'settings', 'checkout', 'customization'],
    path: '/checkout',
    icon: ShoppingCart,
    rbacPermission: 'TAB_CUSTOMISE_UI',
    category: 'secondary',
    journeyStep: JourneyStep.CHECKOUT,
    children: [
      {
        title: 'Core Setup',
        searchText: ['checkout', 'setup', 'checkout', 'configuration', 'checkout', 'editor'],
        path: '/style/elements',
        rbacPermission: 'CORE_SETUP_CHECKOUT',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'Brand',
        searchText: ['brand', 'branding', 'customization', 'theme', 'colors', 'logo'],
        path: '/style/brand',
        rbacPermission: 'BRAND_DETAILS_CHECKOUT',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'Login Page',
        searchText: ['login', 'page', 'authentication', 'customer', 'accounts'],
        path: '/style/login',
        rbacPermission: 'LOGIN_PAGE_CHECKOUT',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'Coupons',
        searchText: ['coupons', 'discount', 'codes', 'voucher', 'promo'],
        path: '/style/coupons',
        rbacPermission: 'COUPONS_PAGE_CHECKOUT',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'Address Page',
        searchText: ['address', 'page', 'shipping', 'address', 'delivery', 'details'],
        path: '/style/address',
        rbacPermission: 'ADDRESS_PAGE',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'Payment Page',
        searchText: ['payment', 'page', 'payment', 'methods', 'checkout', 'payments', 'gateways'],
        path: '/style/payment',
        rbacPermission: 'PAYMENTS_PAGE',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'Order Confirmation',
        searchText: ['order', 'confirmation', 'thank', 'you', 'page', 'receipt'],
        path: '/style/order-confirmation',
        rbacPermission: 'TAB_THANK_YOU_PAGE',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'Shipping Zone',
        searchText: ['shipping', 'zone', 'delivery', 'zones', 'shipping', 'settings'],
        path: '/shipping-delivery',
        rbacPermission: 'TAB_SHIPPING_DELIVERY_V2',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'RTO',
        searchText: ['rto', 'return', 'to', 'origin', 'returns', 'exchanges', 'codtoprepaid', 'reduce', 'cod'],
        path: '/rto-suite',
        rbacPermission: 'TAB_RTO_SUITE',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'Upsell',
        searchText: ['upsell', 'cross', 'sell', 'recommended', 'products'],
        path: '/upsell',
        rbacPermission: ['TAB_UPSELL_LITE', 'TAB_UPSELL'],
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'A/B Experiments',
        searchText: ['ab', 'experiments', 'testing', 'split', 'test'],
        path: '/experiments',
        rbacPermission: 'TAB_EXPERIMENTS',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'Category Insights',
        searchText: ['category', 'insights', 'product', 'insights', 'product', 'analytics'],
        path: '/insights',
        rbacPermission: 'TAB_CATEGORIES_INSIGHTS',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'Quick Add-Ons',
        searchText: ['one', 'click', 'add', 'on', 'quick', 'add', 'cart', 'upsell'],
        path: '/quick-add-on',
        rbacPermission: 'TAB_ONE_CLICK_ADDON',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'Subscriptions',
        searchText: ['subscriptions', 'subscribe', 'save', 'recurring', 'plans', 'autopay'],
        path: '/subscriptions',
        rbacPermission: 'TAB_SUBSCRIPTIONS',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'Shopify Bundles',
        searchText: ['shopify', 'bundles'],
        path: '/shopify-bundles',
        rbacPermission: 'TAB_SHOPIFY_BUNDLES',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      },
      {
        title: 'Gift Cards',
        searchText: ['gift', 'cards', 'giftcard', 'voucher'],
        path: '/gift-cards',
        rbacPermission: '',
        category: 'secondary',
        journeyStep: JourneyStep.CHECKOUT
      }
    ]
  },

  {
    title: 'Shop Pass',
    searchText: ['shop', 'pass', 'sso', 'single', 'sign', 'on', 'login', 'identity'],
    path: '/sso',
    icon: Ticket,
    rbacPermission: 'TAB_SSO',
    category: 'secondary',
    journeyStep: JourneyStep.SHOP_PASS,
    children: [
      {
        title: 'Core Setup',
        searchText: ['shop', 'pass', 'setup', 'sso', 'setup'],
        path: '/sso/setup',
        rbacPermission: 'VIEW_SSO_SETUP',
        category: 'secondary',
        journeyStep: JourneyStep.SHOP_PASS
      },
      {
        title: 'Analytics',
        searchText: ['shop', 'pass', 'analytics', 'sso', 'analytics'],
        path: '/sso/analytics',
        rbacPermission: 'VIEW_SSO_ANALYTICS',
        category: 'secondary',
        journeyStep: JourneyStep.SHOP_PASS
      },
      {
        title: 'Identified Contacts',
        searchText: ['identified', 'contacts', 'sso', 'contacts'],
        path: '/sso/identified-contacts',
        rbacPermission: 'SHOPPASS_REPORT_TAB',
        category: 'secondary',
        journeyStep: JourneyStep.SHOP_PASS
      }
    ]
  },

  {
    title: 'App Store',
    searchText: ['app', 'store', 'explore', 'apps', 'marketplace', 'extensions'],
    path: '/app-store',
    icon: DiamondsFour,
    rbacPermission: 'TAB_APP_STORE',
    category: 'footer'
  },
  {
    title: 'Settings',
    searchText: ['settings', 'configuration', 'preferences', 'account', 'store', 'settings'],
    path: '/settings',
    icon: Gear,
    rbacPermission: 'TAB_SETTINGS',
    category: 'footer',
    children: [
      {
        title: 'Organisational Details',
        searchText: ['organisational', 'details', 'company', 'details', 'business', 'info'],
        path: '/settings/organisational-details',
        rbacPermission: 'EDIT_SETUP_SETTINGS',
        category: 'footer'
      },
      {
        title: 'Connect your shop',
        searchText: ['connect', 'shop', 'shop', 'setup', 'sales', 'channel'],
        path: '/settings/shop-details',
        rbacPermission: 'EDIT_SETUP_SETTINGS',
        category: 'footer'
      },
      {
        title: 'Payment Settings',
        searchText: ['payment', 'settings', 'payment', 'gateways', 'billing', 'methods'],
        path: '/settings/payments',
        rbacPermission: 'EDIT_SHOP_SETTINGS',
        category: 'footer'
      },
      {
        title: 'Channels',
        searchText: ['channels', 'sales', 'channels', 'marketplace', 'integrations'],
        path: '/settings/channels',
        rbacPermission: 'CHANNEL',
        category: 'footer'
      },
      {
        title: 'Integrations',
        searchText: ['integrations', 'third', 'party', 'integrations', 'apps'],
        path: '/apps-integrations',
        rbacPermission: 'EDIT_SHOP_SETTINGS',
        category: 'footer'
        // children: [
        //   {
        //     title: 'Google Ads',
        //     searchText: ['google', 'ads', 'integration', 'marketing'],
        //     path: '/settings/integrations?s_id=google_ad',
        //     rbacPermission: 'TAB_SETTINGS',
        //     category: 'footer'
        //   },
        //   {
        //     title: 'Google Analytics',
        //     searchText: ['google', 'analytics', 'integration', 'tracking'],
        //     path: '/settings/integrations?s_id=ga',
        //     rbacPermission: 'TAB_SETTINGS',
        //     category: 'footer'
        //   },
        //   {
        //     title: 'Google Tag Manager',
        //     searchText: ['google', 'tag', 'manager', 'gtm', 'integration', 'scripts'],
        //     path: '/settings/integrations?s_id=gtm',
        //     rbacPermission: 'TAB_SETTINGS',
        //     category: 'footer'
        //   },
        //   {
        //     title: 'Facebook Pixel',
        //     searchText: ['facebook', 'pixel', 'integration', 'ads', 'tracking'],
        //     path: '/settings/integrations?s_id=facebook',
        //     rbacPermission: 'TAB_SETTINGS',
        //     category: 'footer'
        //   },
        //   {
        //     title: 'WebEngage',
        //     searchText: ['webengage', 'integration', 'crm', 'marketing', 'automation'],
        //     path: '/settings/integrations?s_id=webengage',
        //     rbacPermission: 'TAB_SETTINGS',
        //     category: 'footer'
        //   },
        //   {
        //     title: 'Contlo',
        //     searchText: ['contlo', 'integration', 'crm', 'marketing', 'automation'],
        //     path: '/settings/integrations?s_id=contlo',
        //     rbacPermission: 'TAB_SETTINGS',
        //     category: 'footer'
        //   },
        //   {
        //     title: 'Moengage',
        //     searchText: ['moengage', 'integration', 'crm', 'marketing', 'automation'],
        //     path: '/settings/integrations?s_id=moengage',
        //     rbacPermission: 'TAB_SETTINGS',
        //     category: 'footer'
        //   },
        //   {
        //     title: 'Clevertap',
        //     searchText: ['clevertap', 'integration', 'crm', 'marketing', 'automation'],
        //     path: '/settings/integrations?s_id=clevertap',
        //     rbacPermission: 'TAB_SETTINGS',
        //     category: 'footer'
        //   },
        //   {
        //     title: 'Limechat',
        //     searchText: ['limechat', 'integration', 'crm', 'marketing', 'automation'],
        //     path: '/settings/integrations?s_id=limechat',
        //     rbacPermission: 'TAB_SETTINGS',
        //     category: 'footer'
        //   },
        //   {
        //     title: 'Abandoned Cart Webhooks',
        //     searchText: ['abandoned', 'cart', 'webhook', 'abandoned', 'cart', 'webhook'],
        //     path: '/settings/integrations?s_id=webhooks',
        //     rbacPermission: 'TAB_SETTINGS',
        //     category: 'footer'
        //   }
        // ]
      },
      {
        title: 'Members and Roles',
        searchText: ['members', 'roles', 'team', 'management', 'staff', 'accounts'],
        path: '/settings/members',
        rbacPermission: 'EDIT_ACCOUNTS_SETTINGS',
        category: 'footer'
      },
      {
        title: 'Billing and Usage',
        searchText: ['billing', 'usage', 'subscription', 'invoice', 'plan'],
        path: '/settings/billing-usage',
        rbacPermission: 'MANAGE_INVOICE',
        category: 'footer'
      },
      {
        title: 'My Profile',
        searchText: ['my', 'profile', 'user', 'profile', 'account'],
        path: '/settings/profile',
        rbacPermission: 'EDIT_ACCOUNTS_SETTINGS',
        category: 'footer'
      },
      {
        title: 'Activity Logs',
        searchText: ['activity', 'logs', 'audit', 'logs', 'history'],
        path: '/settings/activity-logs',
        rbacPermission: 'EDIT_ACCOUNTS_SETTINGS',
        category: 'footer'
      }
    ]
  },
  {
    title: 'Support',
    searchText: ['support', 'help', 'contact', 'docs', 'guides'],
    path: '/support',
    icon: Question,
    rbacPermission: 'TAB_SUPPORT',
    category: 'footer'
  },
  {
    title: 'Billing',
    searchText: ['billing', 'subscription', 'payment', 'invoices', 'plan'],
    path: '/billing',
    icon: MoneyWavy,
    rbacPermission: 'TAB_INVOICES',
    category: 'footer'
  }
]
